/*
=========================================================
 * Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

function DefaultFooter({ content }) {
  const { brand, socials, menus, copyright } = content;

  return (
    <MKBox component="footer">
      <Container>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} sx={{ mb: 3 }}>
            <MKBox
              mb={2}
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              gap={4}
            >
              {/* Logo on the left */}
              <Link to={brand.route}>
                <MKBox component="img" src={brand.image} alt={brand.name} maxWidth="5rem" />
              </Link>
              {/* Brand name and socials */}
              <MKBox>
                <MKTypography variant="h6" sx={{ fontWeight: "bold" }}>
                  {brand.name}
                </MKTypography>
                <MKBox display="flex" alignItems="center" mt={1}>
                  {socials.map(({ icon, link }, key) => (
                    <MKTypography
                      key={link}
                      component="a"
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      variant="h5"
                      color="dark"
                      opacity={0.8}
                      mr={key === socials.length - 1 ? 0 : 2.5}
                    >
                      {icon}
                    </MKTypography>
                  ))}
                </MKBox>
              </MKBox>
              {/* Contact Lines section */}
              <MKBox gap={5} mt={2}>
                <MKTypography
                  variant="button"
                  fontWeight="bold"
                  textTransform="capitalize"
                  mb={1}
                  display="block"
                >
                  Contact Lines
                </MKTypography>
                <MKTypography variant="body2" color="text" display="block">
                  Toll Free: 1-800-818-3215
                </MKTypography>
                <MKTypography variant="body2" color="text" display="block">
                  Fax: 800-691-2503
                </MKTypography>
                <MKTypography variant="body2" color="text" display="block">
                  Cell: (310) 387-1315
                </MKTypography>
              </MKBox>
            </MKBox>
          </Grid>
          {/* Add this block right after the menus.map */}
          {menus.map(({ name: title, items }) => (
            <Grid key={title} item xs={6} md={2} sx={{ mb: 3 }}>
              <MKTypography
                display="block"
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
                mb={1}
              >
                {title}
              </MKTypography>
              <MKBox component="ul" p={0} m={0} sx={{ listStyle: "none" }}>
                {items.map(({ name, route, href }) => (
                  <MKBox key={name} component="li" p={0} m={0} lineHeight={1.25}>
                    {href ? (
                      <MKTypography
                        component="a"
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        variant="button"
                        fontWeight="regular"
                        textTransform="capitalize"
                      >
                        {name}
                      </MKTypography>
                    ) : (
                      <MKTypography
                        component={Link}
                        to={route}
                        variant="button"
                        fontWeight="regular"
                        textTransform="capitalize"
                      >
                        {name}
                      </MKTypography>
                    )}
                  </MKBox>
                ))}
              </MKBox>
            </Grid>
          ))}
          <Grid item xs={12} sx={{ textAlign: "center", my: 3 }}>
            {copyright}
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

// Typechecking props for the DefaultFooter
DefaultFooter.propTypes = {
  content: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])).isRequired,
};

export default DefaultFooter;
